import React, {FC, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {AsyncLoader} from '../../../../../../../../src/components/common/loaders/asyncLoader';
import {
  buildContactsManagerAccountsUrl,
  buildContactsManagerSharedContactsUrl,
  buildContactsManagerSyncsUrl,
} from '../../../../../../../../src/helpers/contactsManagerHelpers';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../../../../../../src/models/api/crmSyncModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../../../src/services/tracking';
import {
  generateNavigationBlockerId,
  NavigationBlockerId,
} from '../../../../../../../../src/store/panels/app/appPanelHelpers';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {useNavigationBlocking} from '../../../../../../../../srcCommon/app/navigationBlocking';
import {Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {usePrevious} from '../../../../../../../shared/helpers/src/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncHandler, Handler} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../shared/helpers/src/types/asyncStatuses';
import {Button} from '../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../../shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {PageWrapper} from '../../common/pageWrapper';
import {UnsavedChangesDialog} from '../../common/unsavedChangesDialog';
import {ContactsManagerHeader} from '../../contactsManagerHeader';
import {CrmSyncsErrors} from './crmSyncsErrors/crmSyncsErrors';
import {CrmSyncsFormModel} from './crmSyncsForm';
import {useCreateCrmSync, useUpdateCrmSync} from './crmSyncsHooks';
import {CrmSyncsInfoBoxesContainer} from './crmSyncsInfoBoxes/crmSyncsInfoBoxesContainer';
import {CrmSyncOptionsEnum} from './crmSyncsOptions/crmSyncsOptions';
import {CrmSyncsPageConfig, useCrmPageConfig} from './crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncsPageContainerProps} from './crmSyncsPageContainer';
import {CrmSyncsPageTabs} from './crmSyncsPageTabs';
import {CrmSyncSettingsFormModel} from './crmSyncsSettings/crmSyncsSettings';
import {CrmSyncsSettingsContainer} from './crmSyncsSettings/crmSyncsSettingsContainer';

/*
 * Styles.
 */

const StyledTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledAsyncLoaderWrapperDiv = styled.div`
  margin-top: 100px;
`;

/*
 * Component.
 */

export const CrmSyncsPage: FC<CrmSyncsPageContainerProps> = (props) => {
  const {
    panelId,
    source,
    previousPageType,
    isConnected,
    accountCrmSync,
    cardCrmSync,
    isOnErrorsPage,
    hasCrmSyncFullPageSalesforce,
    hasMicrosoftDynamicAccountSync,
    externalAccountBaseUrl,
    externalCardBaseUrl,
    isAdmin,
    isCrmSyncLoading,
    isCrmSyncsFetchFailed,
    isSyncing,
    hasCrmSyncFeature,
    hasHubspotContactSync,
    hasOutlookDirectorySync,
    navigateToLink,
    fetchCrmSyncsAsync,
  } = props;

  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CRM_SYNC_VIEW, {source});
  });

  const config = useCrmPageConfig(source);
  const {logo, displayName, card: cardConfig, account: accountConfig} = config;
  // we want to keep the value of the previous list page we came from and don't want it overwritten by any syncs page
  const [savedPreviousPage] = useState(computePreviousPageType(Boolean(cardConfig), previousPageType));

  const [hasExpiredCredentials, setHasExpiredCredentials] = useState<boolean>(false);
  const previousIsConnected = usePrevious(isConnected);
  useEffect(() => {
    // reset expired credentials whenever isConnected state changes
    if (!previousIsConnected && isConnected) {
      setHasExpiredCredentials(false);
    }
  }, [isConnected, previousIsConnected, setHasExpiredCredentials]);

  const [updatedSettingFormState, setUpdatedSettingsFormState] = useState<
    CrmSyncSettingsFormModel | undefined
  >(undefined);
  const cardForm = updatedSettingFormState?.card;
  const accountForm = updatedSettingFormState?.account;

  // try to create syncs if we don't have them
  const {hasErrorCreatingSync, onCreateCrmSyncAsync} = useCreateCrmSync(
    source,
    isConnected,
    isCrmSyncLoading,
    Boolean(accountCrmSync),
    Boolean(cardCrmSync),
  );

  const isAccountSyncEnabledOnServer = accountCrmSync?.status === CrmSyncStatusesEnum.ENABLED;
  const isCardSyncEnabledOnServer = cardCrmSync?.status === CrmSyncStatusesEnum.ENABLED;
  const {isUpdatingCrmSync: isUpdatingAccountCrmSync, onUpdateCrmSync: onUpdateAccountCrmSync} =
    useUpdateCrmSync(
      panelId,
      source,
      CrmSyncIntegrationResourceTypesEnum.ACCOUNT,
      isAccountSyncEnabledOnServer,
      accountCrmSync?.id,
      updatedSettingFormState,
    );

  const {isUpdatingCrmSync: isUpdatingCardCrmSync, onUpdateCrmSync: onUpdateCardCrmSync} = useUpdateCrmSync(
    panelId,
    source,
    CrmSyncIntegrationResourceTypesEnum.CARD,
    isCardSyncEnabledOnServer,
    cardCrmSync?.id,
    updatedSettingFormState,
  );

  const hasAccountFormChange = Boolean(accountForm);
  const hasCardFormChange = Boolean(cardForm);

  const isFormInvalid = Boolean(updatedSettingFormState?.isFormInvalid);
  const isValidConnected = isConnected && !hasExpiredCredentials;

  const canSyncAccount = computeCanSync(
    isUpdatingAccountCrmSync,
    !isCrmSyncLoading,
    isFormInvalid,
    isValidConnected,
    isAdmin,
    accountForm,
  );
  const canSyncCard = computeCanSync(
    isUpdatingCardCrmSync,
    !isCrmSyncLoading,
    isFormInvalid,
    isValidConnected,
    isAdmin,
    cardForm,
  );
  const canSync = computeCanSyncAllSyncs(config, canSyncAccount, canSyncCard);

  const canSyncOrSave = (hasAccountFormChange || hasCardFormChange) && canSync;

  // generate our own id so we do not block navigation when you click save/sync
  const navigationBlockerId = useMemo(() => generateNavigationBlockerId(), []);
  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(
    panelId,
    canSyncOrSave,
    navigationBlockerId,
  );

  const [isCardErrorsExpandedByDefault, setIsCardErrorsExpandedByDefault] = useState(true);
  const [isAccountErrorsExpandedByDefault, setIsAccountErrorsExpandedDefault] = useState(true);
  const onViewErrorsClick = async (resourceType?: CrmSyncIntegrationResourceTypesEnum) => {
    // if they click directly on the errors tab open both error sections
    if (!resourceType) {
      setIsCardErrorsExpandedByDefault(true);
      setIsAccountErrorsExpandedDefault(true);
    } else {
      // if they click view errors from within a form, open for that resource only
      const isCard = resourceType === CrmSyncIntegrationResourceTypesEnum.CARD;
      setIsCardErrorsExpandedByDefault(isCard);
      setIsAccountErrorsExpandedDefault(!isCard);
    }
    navigateToLink(buildContactsManagerSyncsUrl(source, true), undefined, navigationBlockerId);
  };

  const onSaveOrSync = async () => {
    await onUpdateAccountCrmSync();
    await onUpdateCardCrmSync();
  };

  const onSaveOrSyncAndNavigate = async () => {
    await onSaveOrSync();
    navigateToPreviousPage(navigationBlockerId);
  };

  const onSyncSettingsTabClick = async () => {
    navigateToLink(buildContactsManagerSyncsUrl(source), undefined, navigationBlockerId);
  };

  const navigateToPreviousPage = (ignoreNavigationBlockerId?: NavigationBlockerId) => {
    if (savedPreviousPage === ContactsManagerParametersTypesEnum.SHARED_CONTACTS) {
      navigateToLink(buildContactsManagerSharedContactsUrl(), undefined, ignoreNavigationBlockerId);
      return;
    }
    navigateToLink(buildContactsManagerAccountsUrl(), undefined, ignoreNavigationBlockerId);
  };

  // navigate away if we don't have the feature flag or correct source
  if (
    (source === CrmSyncSourcesEnum.SALESFORCE && !hasCrmSyncFullPageSalesforce) ||
    (source === CrmSyncSourcesEnum.MICROSOFT_DYNAMICS && !hasMicrosoftDynamicAccountSync) ||
    (source === CrmSyncSourcesEnum.HUBSPOT && !hasHubspotContactSync) ||
    (source === CrmSyncSourcesEnum.OUTLOOK_DIRECTORY && !hasOutlookDirectorySync)
  ) {
    navigateToPreviousPage();
  }

  const isCardSyncEnabled = computeIsSyncEnabled(isCardSyncEnabledOnServer, cardForm);
  const isAccountSyncEnabled = computeIsSyncEnabled(isAccountSyncEnabledOnServer, accountForm);
  const canSyncNow = (isCardSyncEnabled || isAccountSyncEnabled) && canSync;

  const shouldHideFooter = isOnErrorsPage || isCrmSyncLoading || !hasCrmSyncFeature;

  const hasAccountSyncErrors = Boolean(accountCrmSync?.errors && accountCrmSync.errors.length > 0);
  const hasCardSyncErrors = Boolean(cardCrmSync?.errors && cardCrmSync.errors.length > 0);
  const hasSyncErrors = hasAccountSyncErrors || hasCardSyncErrors;

  if (!isCrmSyncLoading && isOnErrorsPage && (!hasSyncErrors || !isConnected)) {
    // redirect from errors page if there are no errors to show or we are disconnected
    navigateToLink(buildContactsManagerSyncsUrl(source));
  }

  const shouldHideTabs =
    !hasSyncErrors ||
    !isConnected ||
    !hasCrmSyncFeature ||
    (!isCardSyncEnabledOnServer && !isAccountSyncEnabledOnServer);

  const maybeRenderSettings = () => {
    if (isCrmSyncsFetchFailed) {
      return (
        <StyledAsyncLoaderWrapperDiv>
          <AsyncLoader status={AsyncStatusesEnum.FAILED} onRetryClick={fetchCrmSyncsAsync} />
        </StyledAsyncLoaderWrapperDiv>
      );
    }
    // if they have an error creating a sync, and they don't have the syncs
    if (hasErrorCreatingSync && ((!cardCrmSync && cardConfig) || (!accountCrmSync && accountConfig))) {
      return (
        <StyledAsyncLoaderWrapperDiv>
          <AsyncLoader status={AsyncStatusesEnum.FAILED} onRetryClick={() => onCreateCrmSyncAsync(true)} />
        </StyledAsyncLoaderWrapperDiv>
      );
    }

    return (
      <CrmSyncsSettingsContainer
        panelId={panelId}
        source={source}
        isSavingOrSyncing={isUpdatingAccountCrmSync || isUpdatingCardCrmSync || isSyncing}
        hasExpiredCredentials={hasExpiredCredentials}
        setHasExpiredCredentials={setHasExpiredCredentials}
        initialFormState={updatedSettingFormState}
        onChange={setUpdatedSettingsFormState}
        onViewErrorsClick={onViewErrorsClick}
      />
    );
  };

  return (
    <PageWrapper
      header={renderHeader(logo, displayName, navigateToPreviousPage, savedPreviousPage)}
      pageTabs={maybeRenderPageTabs(
        shouldHideTabs,
        isOnErrorsPage,
        onViewErrorsClick,
        onSyncSettingsTabClick,
      )}
      footer={
        shouldHideFooter
          ? null
          : renderFooter(
              canSyncOrSave,
              isAccountSyncEnabledOnServer || isCardSyncEnabledOnServer,
              onSaveOrSyncAndNavigate,
              () => navigateToPreviousPage(navigationBlockerId), // we don't want to block navigation when you click sync
            )
      }
      sideContent={
        isCrmSyncLoading ? null : (
          <CrmSyncsInfoBoxesContainer
            source={source}
            canSyncNow={canSyncNow}
            onSync={onSaveOrSyncAndNavigate}
            showNextSync={computeShouldShowNextSync(
              isValidConnected,
              isAccountSyncEnabledOnServer,
              isCardSyncEnabledOnServer,
              updatedSettingFormState,
            )}
          />
        )
      }
    >
      <>
        {isOnErrorsPage && !isCrmSyncLoading ? (
          <CrmSyncsErrors
            accountSyncErrors={accountCrmSync?.errors || []}
            cardSyncErrors={cardCrmSync?.errors || []}
            source={source}
            externalAccountBaseUrl={externalAccountBaseUrl}
            externalCardBaseUrl={externalCardBaseUrl}
            isCardErrorsExpandedByDefault={isCardErrorsExpandedByDefault}
            isAccountErrorsExpandedByDefault={isAccountErrorsExpandedByDefault}
          />
        ) : (
          maybeRenderSettings()
        )}
        {didBlockNavigation && (
          <UnsavedChangesDialog
            onDiscard={() => {
              setUpdatedSettingsFormState(undefined);
              unblockNavigation();
            }}
            onClose={() => {
              cancelNavigation();
            }}
          />
        )}
      </>
    </PageWrapper>
  );
};

/*
 * Helpers.
 */

function computePreviousPageType(
  hasCardConfig: boolean,
  previousPageType?: ContactsManagerParametersTypesEnum,
) {
  // previous page type is undefined if you navigate directly to the page
  if (previousPageType) {
    return previousPageType;
  }
  if (hasCardConfig) {
    return ContactsManagerParametersTypesEnum.SHARED_CONTACTS;
  }
  return ContactsManagerParametersTypesEnum.ACCOUNTS;
}

function isListViewValid(formState?: CrmSyncsFormModel) {
  if (!formState) {
    return true;
  }
  // if they chose the list option make sure they have selected a list id
  if (formState?.selectedListOption.type === CrmSyncOptionsEnum.LISTS) {
    return Boolean(formState?.selectedListOption.listId);
  }
  return Boolean(formState?.selectedListOption.type);
}

function isMappingValid(formState?: CrmSyncsFormModel) {
  if (!formState) {
    return true;
  }

  // if we encounter an undefined customFieldId the mapping is not valid
  return !formState.mappedFields.some((field) => field.customFieldId === undefined);
}

function renderBreadcrumbLabel(previousPage?: ContactsManagerParametersTypesEnum) {
  if (previousPage === ContactsManagerParametersTypesEnum.SHARED_CONTACTS) {
    return <Trans id="crm-sync-page-breadcrumb-label-shared-contacts">Shared contacts</Trans>;
  }
  return <Trans id="crm-sync-page-breadcrumb-label-accounts">Accounts</Trans>;
}

function renderHeader(
  logo: string,
  displayName: string,
  onBreadcrumbClick: Handler,
  previousPage?: ContactsManagerParametersTypesEnum,
) {
  return (
    <ContactsManagerHeader
      title={
        <StyledTitle>
          <img src={logo} alt="crm logo" height={24} width={24} />
          <Trans id="crm-sync-page-title">Sync from {displayName}</Trans>
        </StyledTitle>
      }
      topLevelBreadcrumb={{
        label: renderBreadcrumbLabel(previousPage),
        onBreadcrumbClick,
      }}
      interactionId="crm-sync-page-header"
    />
  );
}

function maybeRenderPageTabs(
  shouldHideTabs: boolean,
  isOnErrorsPage: boolean,
  onViewErrorsClick: Handler,
  onSyncSettingsClick: Handler,
) {
  if (shouldHideTabs) {
    return null;
  }
  return (
    <CrmSyncsPageTabs
      isOnErrorsPage={isOnErrorsPage}
      onViewErrorsClick={onViewErrorsClick}
      onSyncSettingsClick={onSyncSettingsClick}
    />
  );
}

function renderFooter(
  canSyncOrSave: boolean,
  isSyncsEnabledOnServer: boolean,
  saveAndSync: AsyncHandler,
  navigateToPreviousPage: Handler,
) {
  return (
    <ButtonGroup>
      <Button isRounded onClick={navigateToPreviousPage}>
        <Trans id="crm-sync-page-cancel-button">Cancel</Trans>
      </Button>
      <Button
        isRounded
        isDisabled={!canSyncOrSave}
        variant={ButtonVariantsEnum.PRIMARY}
        onClick={saveAndSync}
      >
        {/* When editing an existing sync we show the text as save changes */}
        {isSyncsEnabledOnServer ? (
          <Trans id="crm-sync-page-save-button">Save changes</Trans>
        ) : (
          <Trans id="crm-sync-page-sync-button">Sync</Trans>
        )}
      </Button>
    </ButtonGroup>
  );
}

function computeCanSync(
  isUpdatingCrmSync: boolean,
  isCrmSyncLoaded: boolean,
  isFormInvalid: boolean,
  isValidConnected: boolean,
  isAdmin: boolean,
  formState?: CrmSyncsFormModel,
) {
  return (
    !isUpdatingCrmSync &&
    isCrmSyncLoaded &&
    computeIsFormValid(formState) &&
    !isFormInvalid &&
    isValidConnected &&
    isAdmin
  );
}

function computeCanSyncAllSyncs(config: CrmSyncsPageConfig, canSyncAccount: boolean, canSyncCard: boolean) {
  if (config.account && config.card) {
    return canSyncAccount && canSyncCard;
  }

  if (config.account) {
    return canSyncAccount;
  }

  if (config.card) {
    return canSyncCard;
  }
  return false;
}

function computeIsSyncEnabled(isSyncEnabledOnServer: boolean, formState?: CrmSyncsFormModel) {
  if (!formState) {
    return isSyncEnabledOnServer;
  }
  return Boolean(formState?.isEnabled);
}

function computeIsFormValid(formState?: CrmSyncsFormModel) {
  // if the form is disabled consider it valid
  if (formState && !formState.isEnabled) {
    return true;
  }
  return isListViewValid(formState) && isMappingValid(formState);
}

function computeShouldShowNextSync(
  isValidConnected: boolean,
  isAccountSyncEnabledOnServer: boolean,
  isCardSyncEnabledOnServer: boolean,
  updatedSettingFormState?: CrmSyncSettingsFormModel,
) {
  if (!isValidConnected) {
    return false;
  }

  return (
    computeIsSyncEnabled(isAccountSyncEnabledOnServer, updatedSettingFormState?.account) ||
    computeIsSyncEnabled(isCardSyncEnabledOnServer, updatedSettingFormState?.card)
  );
}
